import { FormEvent, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { api, ApiError } from '../api'

const Register = () => {
  const { isAuthenticated, ready, refresh } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [gdprConsent, setGdprConsent] = useState(true)
  const [aiAck, setAiAck] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const next = (location.state as { from?: string } | null)?.from ?? '/dashboard'

  if (!ready) {
    return (
      <section className="auth-page">
        <p>Preparing registration…</p>
      </section>
    )
  }

  if (isAuthenticated) {
    return <Navigate to={next} replace />
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!email.trim() || !password.trim()) {
      setError('Email and password are required.')
      return
    }

    if (confirmPassword.trim() !== password.trim()) {
      setError('Passwords do not match.')
      return
    }

    if (!gdprConsent || !aiAck) {
      setError('You need to accept privacy and AI use notices to create an account.')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      const normalizedEmail = email.trim()
      const response = await api.authRegister({
        email: normalizedEmail,
        username: normalizedEmail,
        password,
        gdpr_consent: gdprConsent,
        ai_ack: aiAck,
      })
      const token = response?.access_token
      if (token) {
        window.localStorage.setItem('pit_jwt', token)
        refresh()
        navigate(next, { replace: true })
        return
      }

      navigate('/login', {
        replace: true,
        state: { message: 'Account created. Please log in to continue.' },
      })
    } catch (err) {
      const message = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Registration failed.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="auth-shell auth-register">
      <div className="auth-hero-panel">
        <div className="auth-badge-row">
          <span className="auth-pill">Premium access</span>
          <span className="auth-pill ghost">Privacy links</span>
        </div>
        <h1>Create your Places in Time account</h1>
        <p className="lead">
          Unlock curated conversations, saved threads, and continuity across devices. We aim to handle account data
          clearly and responsibly, and we link our privacy, terms, and legal information below.
        </p>
        <ul className="auth-highlights">
          <li>Save threads and return to them later</li>
          <li>Adjust tone and knowledge settings</li>
          <li>Use one account across sessions and devices</li>
        </ul>
        <div className="auth-legal">
          <Link to="/privacy-policy-gdpr">Privacy & GDPR</Link>
          <span aria-hidden="true">•</span>
          <Link to="/terms-of-use">Terms of Use</Link>
          <span aria-hidden="true">•</span>
          <Link to="/impressum">Impressum</Link>
        </div>
      </div>

      <div className="auth-card" role="form" aria-labelledby="register-heading">
        <div className="auth-card-header">
          <p className="eyebrow">Account access</p>
          <h2 id="register-heading">Create your account</h2>
          <p className="muted">You will be redirected to your dashboard as soon as we confirm your credentials.</p>
        </div>

        {error && <p className="auth-alert error">{error}</p>}

        <form className="auth-form" onSubmit={handleSubmit}>
          <label htmlFor="register-username">Email address</label>
          <input
            id="register-username"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            placeholder="e.g., ada@places-in-time.com"
          />

          <label htmlFor="register-password">Password</label>
          <input
            id="register-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            placeholder="Create a strong password"
          />

          <label htmlFor="register-confirm-password">Confirm password</label>
          <input
            id="register-confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            required
            placeholder="Repeat password"
          />

          <div className="auth-actions">
            <label className="consent-row">
              <input
                type="checkbox"
                checked={gdprConsent}
                onChange={(event) => setGdprConsent(event.target.checked)}
                required
              />
              <span>
                I have read the <Link to="/privacy-policy-gdpr">Privacy Policy</Link> and agree to the processing of my
                account data as described there.
              </span>
            </label>

            <label className="consent-row">
              <input
                type="checkbox"
                checked={aiAck}
                onChange={(event) => setAiAck(event.target.checked)}
                required
              />
              <span>I understand that responses are AI-generated and may be incomplete or inaccurate.</span>
            </label>

            <button type="submit" className="button primary" disabled={submitting}>
              {submitting ? 'Creating account…' : 'Register'}
            </button>
            <p className="form-legal">
              By creating an account you agree to our <Link to="/privacy-policy-gdpr">Privacy Policy</Link> and
              <Link to="/terms-of-use"> Terms of Use</Link>.
            </p>
          </div>
        </form>

        <div className="auth-alt">
          <div>
            <p className="muted">Already registered?</p>
            <p className="muted">Sign back in to resume your threads.</p>
          </div>
          <Link className="button ghost" to="/login" state={{ from: location }}>
            Log in
          </Link>
        </div>
      </div>
    </section>
  )
}

export default Register
